import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, Divider, Stack } from "@mui/material";
import { useStyle } from "../../context/styleContext/styleContext";
import ReactGA from "react-ga4";
import Seo from "../../components/Seo";

export default function PrivacyPolicy() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Privacy Policy | Parish Audio",
    url: "https://parishaudio.com/privacy-policy",
    description:
      "Read the Parish Audio privacy policy to learn how we collect, use, and protect your information.",
  };

  const sectionTitleSx = {
    fontSize: { xs: "1.1rem", md: "1.25rem" },
    fontWeight: 800,
    color: style.color4,
    mt: 1,
    mb: 1,
  };

  const bodySx = {
    color: style.color3,
    lineHeight: 1.8,
    fontSize: { xs: "0.95rem", md: "1rem" },
    mb: 1.5,
  };

  const listSx = {
    color: style.color3,
    lineHeight: 1.8,
    fontSize: { xs: "0.95rem", md: "1rem" },
    pl: 3,
    mb: 1.5,
  };

  return (
    <>
      <Seo
        title="Privacy Policy | Parish Audio"
        description="Read the Parish Audio privacy policy to learn how we collect, use, and protect your information."
        canonical="https://parishaudio.com/privacy-policy"
        schema={schema}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1000, mx: "auto", minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, md: 2.25 },
                py: { xs: 2, md: 2.5 },
                backgroundColor:style.darkPanel,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "1.9rem", md: "2.6rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  color: style.color4,
                  letterSpacing: "-0.03em",
                }}
              >
                Privacy Policy
              </Typography>

              <Typography sx={{ ...bodySx, mt: 1, mb: 0 }}>
                Effective date: April 15, 2026
              </Typography>
            </Box>

            <Box sx={{ px: { xs: 1.5, md: 2.25 }, py: { xs: 1.75, md: 2.25 } }}>
              <Typography sx={bodySx}>
                This Privacy Policy explains how Brainwave LLC, doing business as
                Parish Audio, collects, uses, discloses, and protects information
                through parishaudio.com and related products and services,
                including account creation, software downloads, purchases,
                subscriptions, reviews, support, and website analytics.
              </Typography>

              <Typography sx={bodySx}>
                By using our website, creating an account, purchasing a product,
                starting a trial, subscribing, submitting a review, or using our
                software and services, you acknowledge the practices described in
                this Privacy Policy.
              </Typography>

              <Typography sx={sectionTitleSx}>1. Information we collect</Typography>
              <Typography sx={bodySx}>
                We collect information in a few different ways depending on how you
                interact with Parish Audio.
              </Typography>

              <Typography sx={{ ...bodySx, fontWeight: 700, color: style.color4, mb: 0.5 }}>
                Information you provide directly
              </Typography>
              <Box component="ul" sx={listSx}>
                <li>name and email address</li>
                <li>account login information</li>
                <li>billing and purchase details</li>
                <li>subscription and trial details</li>
                <li>reviews, ratings, testimonials, and other content you submit</li>
                <li>messages you send us for support or other inquiries</li>
              </Box>

              <Typography sx={{ ...bodySx, fontWeight: 700, color: style.color4, mb: 0.5 }}>
                Information collected automatically
              </Typography>
              <Box component="ul" sx={listSx}>
                <li>IP address</li>
                <li>browser type and version</li>
                <li>device type and operating system</li>
                <li>referring pages and general usage activity on the website</li>
                <li>dates, times, and pages visited</li>
                <li>cookie and similar technology data where used</li>
              </Box>

              <Typography sx={{ ...bodySx, fontWeight: 700, color: style.color4, mb: 0.5 }}>
                Information related to software licensing and account access
              </Typography>
              <Box component="ul" sx={listSx}>
                <li>license status and product access information</li>
                <li>subscription status and trial status</li>
                <li>machine ID and device authorization information used to manage activations</li>
                <li>IP address and login or authorization activity related to account access and fraud prevention</li>
                <li>basic system information such as operating system, RAM, and related technical environment details used to manage licensing, device limits, compatibility, and abuse prevention</li>
              </Box>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>2. How we use information</Typography>
              <Typography sx={bodySx}>
                We use information we collect to:
              </Typography>
              <Box component="ul" sx={listSx}>
                <li>create and manage accounts</li>
                <li>process purchases, trials, subscriptions, and renewals</li>
                <li>deliver downloads and authorize software access</li>
                <li>manage device activations, enforce license limits, and prevent abuse or fraud</li>
                <li>verify account access and product entitlement</li>
                <li>provide customer support</li>
                <li>display and moderate reviews and ratings</li>
                <li>improve our website, products, and services</li>
                <li>understand website traffic, analytics, and advertising performance</li>
                <li>send transactional emails such as receipts, account notices, and subscription notices</li>
                <li>send marketing emails if you opt in or where otherwise permitted by law</li>
                <li>enforce our terms, protect our rights, and comply with legal obligations</li>
              </Box>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>3. Payments</Typography>
              <Typography sx={bodySx}>
                Payments are processed through third-party payment providers such as
                Stripe. We do not intend to store full payment card details on our
                own servers. Payment processors may collect and process payment and
                related transaction information in accordance with their own privacy
                policies and legal obligations.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>4. Analytics, cookies, and similar technologies</Typography>
              <Typography sx={bodySx}>
                We use cookies and similar technologies where needed to operate the
                website, support account and login functions, measure traffic,
                understand usage, and improve the website and related services.
              </Typography>

              <Typography sx={bodySx}>
                We use Umami analytics to understand website traffic and usage
                patterns. We also use Google advertising and related measurement
                tools to understand ad performance, conversions, and related
                campaign activity.
              </Typography>

              <Typography sx={bodySx}>
                Depending on your settings and consent choices where applicable,
                analytics, advertising, or measurement technologies may collect
                information about your interactions with the website, including
                pages visited, referral sources, device or browser information,
                and actions taken on the site.
              </Typography>

              <Typography sx={bodySx}>
                Some technologies may be necessary for core website functions,
                while others may be used for analytics, measurement, or
                advertising-related purposes.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>5. Reviews and user submissions</Typography>
              <Typography sx={bodySx}>
                If you submit a review, rating, testimonial, or other content to
                our website, we may display that content publicly along with
                information you choose to provide in connection with it. We may
                moderate, screen, reject, or remove reviews or submissions for
                profanity, abuse, spam, fraud, or violations of our Terms and
                Conditions.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>6. How we share information</Typography>
              <Typography sx={bodySx}>
                We may share information with service providers that help us
                operate our business, such as providers involved in payment
                processing, website hosting, analytics, email delivery, customer
                support, security, and related business operations.
              </Typography>

              <Typography sx={bodySx}>
                We may also disclose information:
              </Typography>
              <Box component="ul" sx={listSx}>
                <li>to comply with law, legal process, or lawful requests</li>
                <li>to enforce our agreements and protect our rights</li>
                <li>to investigate fraud, abuse, or security issues</li>
                <li>in connection with a merger, sale, acquisition, or other business transfer</li>
              </Box>

              <Typography sx={bodySx}>
                We do not sell personal information for money.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>7. Email communications</Typography>
              <Typography sx={bodySx}>
                We may send transactional emails related to your account,
                purchases, subscriptions, trials, downloads, support, and product
                access.
              </Typography>

              <Typography sx={bodySx}>
                If you sign up for marketing emails or newsletters in the future,
                we may also send promotional communications. You can unsubscribe
                from marketing emails through the unsubscribe link in those
                messages. Even if you opt out of marketing emails, we may still
                send transactional or service-related messages.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>8. Data retention</Typography>
              <Typography sx={bodySx}>
                We retain personal information for as long as reasonably necessary
                to provide our services, maintain accounts and licenses, process
                transactions, comply with legal obligations, resolve disputes,
                enforce agreements, and protect against fraud or abuse.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>9. Security</Typography>
              <Typography sx={bodySx}>
                We use reasonable administrative, technical, and organizational
                measures designed to protect personal information. No method of
                internet transmission, storage, or electronic processing is
                completely secure, so we cannot guarantee absolute security.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>10. Children’s privacy</Typography>
              <Typography sx={bodySx}>
                Our website, software, and services are not directed to children,
                and we do not knowingly collect personal information from children.
                If you believe a child has provided personal information to us,
                please contact us so we can review and address it.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>11. Your rights and choices</Typography>
              <Typography sx={bodySx}>
                Depending on where you live, you may have rights to request access
                to personal information, request correction, request deletion,
                object to certain processing, or request information about how your
                data is used. We may need to verify your identity before acting on
                a request.
              </Typography>

              <Typography sx={bodySx}>
                California law expects privacy notices to describe categories of
                personal information collected and the purposes for which they are
                used. If you are a California resident and want to make a privacy
                request, please contact us using the information below.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>12. Third-party links and services</Typography>
              <Typography sx={bodySx}>
                Our website may contain links to third-party websites or services.
                We are not responsible for the privacy practices of those third
                parties. We encourage you to review their privacy policies before
                providing information to them.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>13. Changes to this policy</Typography>
              <Typography sx={bodySx}>
                We may update this Privacy Policy from time to time. When we do, we
                will update the effective date at the top of this page. Your
                continued use of the website, software, or services after changes
                are posted means you acknowledge the updated policy, except to the
                extent a different form of notice is required by law.
              </Typography>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Typography sx={sectionTitleSx}>14. Contact</Typography>
              <Typography sx={bodySx}>
                If you have questions about this Privacy Policy or would like to
                make a privacy-related request, please contact:
              </Typography>

              <Stack spacing={0.35} sx={{ mb: 2 }}>
                <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                  Brainwave LLC / Parish Audio
                </Typography>
                <Typography sx={{ color: style.color3 }}>
                  Email: support@parishaudio.com
                </Typography>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}